import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { analyticsAPI, campaignAPI, eventTypesAPI } from '../../services/api';
import { toast } from 'react-hot-toast';
import DashboardLayout from '../../components/layout/DashboardLayout';
import useDocumentTitle from '../../hooks/useDocumentTitle';
import LiveVisitorsHub from '../../components/analytics/LiveVisitorsHub';
import DateRangePicker from '../../components/common/DateRangePicker';
import {
  PieChart, Pie, Cell,
  AreaChart, Area,
  BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer
} from 'recharts';

// Chart colors
const CHART_COLORS = ['#2563EB', '#7C3AED', '#10B981', '#F59E0B', '#EF4444', '#06B6D4', '#EC4899', '#8B5CF6'];

interface EnhancedStats {
  visitors: number;
  pageViews: number;
  events: number;
  bounceRate: number;
  avgSessionDuration: number;
  countryStats: Array<{ country: string; count: number; percentage: number }>;
  deviceStats: Array<{ device: string; count: number; percentage: number }>;
  trafficSources: Array<{ source: string; count: number; percentage: number }>;
  pageViewsTrend: Array<{ date: string; views: number; uniqueVisitors: number }>;
  uniqueVisitorDetails: { total: number; new: number; returning: number };
  avgTimeSpent: number;
  topPages: Array<{ url: string; title: string; views: number; percentage: number }>;
  yearOverYear: {
    currentVisitors: number;
    lastYearVisitors: number;
    visitorChange: number;
    currentPageViews: number;
    lastYearPageViews: number;
    pageViewChange: number;
  } | null;
  growthScore: number;
}

interface ActiveCampaign {
  id: number;
  name: string;
  slug: string;
  visitors_count?: number;
  is_active: number;
}

const SummaryPage: React.FC = () => {
  useDocumentTitle('Analytics Summary');
  const { currentAsset } = useAuth();
  const [stats, setStats] = useState<EnhancedStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('30d');
  const [granularity, setGranularity] = useState<'daily' | 'monthly'>('daily');
  const [activeCampaigns, setActiveCampaigns] = useState<ActiveCampaign[]>([]);
  const [customStart, setCustomStart] = useState<Date | null>(null);
  const [customEnd, setCustomEnd] = useState<Date | null>(null);
  const [showCustomPicker, setShowCustomPicker] = useState(false);
  const [conversionStats, setConversionStats] = useState<any>(null);

  const fetchActiveCampaigns = useCallback(async () => {
    if (!currentAsset) return;
    try {
      const response = await campaignAPI.list(currentAsset.id);
      const data = response.data.data || response.data || [];
      const active = (Array.isArray(data) ? data : []).filter((c: ActiveCampaign) => c.is_active === 1).slice(0, 3);
      setActiveCampaigns(active);
    } catch {
      // silently fail - not critical
    }
  }, [currentAsset]);

  useEffect(() => {
    if (currentAsset) {
      fetchEnhancedStats();
      fetchActiveCampaigns();
      fetchConversionStats();
    } else {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentAsset, period, granularity]);

  const fetchConversionStats = async () => {
    if (!currentAsset) return;
    try {
      const response = await eventTypesAPI.getSummary(period, currentAsset.id);
      const data = response.data.data;
      setConversionStats({
        page_views: data.totals?.total_events || 0,
        unique_visitors: data.totals?.total_unique_users || 0,
        converted_visitors: data.goal_stats?.converted_visitors || 0,
        conversion_rate: data.totals?.total_unique_users > 0
          ? Math.round((data.goal_stats?.converted_visitors / data.totals.total_unique_users) * 1000) / 10
          : 0,
        has_goals: data.goal_stats?.has_goals || false,
        goal_event_names: data.goal_stats?.goal_event_names || [],
      });
    } catch {
      // non-critical
    }
  };

  const fetchEnhancedStats = async (fromDate?: string, toDate?: string) => {
    if (!currentAsset) return;

    setLoading(true);
    try {
      const response = await analyticsAPI.getEnhancedSummary(currentAsset.id, period, granularity, fromDate, toDate);
      setStats(response.data.data);
    } catch (error: any) {
      console.error('Failed to fetch enhanced stats:', error);
      toast.error('Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  };

  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}m ${secs}s`;
  };

  const formatNumber = (num: number) => {
    return new Intl.NumberFormat().format(num);
  };

  // Format number with K, M suffixes (1K, 2.5M, etc.)
  const formatCompact = (num: number): string => {
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1).replace(/\.0$/, '') + 'M';
    }
    if (num >= 1000) {
      return (num / 1000).toFixed(1).replace(/\.0$/, '') + 'K';
    }
    return num.toString();
  };

  const formatDate = (dateStr: any): string => {
    if (!dateStr) return '';
    const strValue = String(dateStr);
    if (granularity === 'monthly') {
      const [year, month] = strValue.split('-');
      return new Date(parseInt(year), parseInt(month) - 1).toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
    }
    const date = new Date(strValue);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const getGrowthScoreColor = (score: number) => {
    if (score >= 70) return 'success';
    if (score >= 40) return 'warning';
    return 'danger';
  };

  const getChangeIcon = (change: number) => {
    if (change > 0) return 'bi-arrow-up-circle-fill text-success';
    if (change < 0) return 'bi-arrow-down-circle-fill text-danger';
    return 'bi-dash-circle text-secondary';
  };

  // Prepare unique visitors data for donut chart
  const uniqueVisitorsData = stats ? [
    { name: 'New', value: stats.uniqueVisitorDetails.new, color: '#10B981' },
    { name: 'Returning', value: stats.uniqueVisitorDetails.returning, color: '#3B82F6' }
  ] : [];

  // Prepare YoY comparison data for bar chart
  const yoyData = stats?.yearOverYear ? [
    {
      metric: 'Visitors',
      'Current Period': stats.yearOverYear.currentVisitors,
      'Last Year': stats.yearOverYear.lastYearVisitors
    },
    {
      metric: 'Page Views',
      'Current Period': stats.yearOverYear.currentPageViews,
      'Last Year': stats.yearOverYear.lastYearPageViews
    }
  ] : [];

  if (!currentAsset && !loading) {
    return (
      <DashboardLayout>
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
          <div className="text-center">
            <i className="bi bi-box-seam fs-1 text-muted d-block mb-3"></i>
            <h4>No Asset Selected</h4>
            <p className="text-muted mb-4">Please add an asset to view analytics</p>
            <a href="/setup/asset" className="btn btn-primary">
              <i className="bi bi-plus-circle me-2"></i>Add Your First Asset
            </a>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  if (loading) {
    return (
      <DashboardLayout>
        <div className="d-flex justify-content-center align-items-center" style={{ height: '400px' }}>
          <div className="text-center">
            <div className="spinner-border text-primary mb-3" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="text-muted">Loading analytics data...</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="container-fluid">
        {/* Page Header */}
        <div className="row mb-4">
          <div className="col-12">
            <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
              <div>
                <h2 className="h3 mb-1">Analytics Summary</h2>
                <p className="text-muted mb-0">Comprehensive overview of your website analytics</p>
              </div>
              <div className="d-flex gap-2 flex-wrap align-items-center">
                {/* Period Selector */}
                <div className="btn-group">
                  <button
                    className={`btn btn-sm ${period === '1d' && !showCustomPicker ? 'btn-primary' : 'btn-outline-secondary'}`}
                    onClick={() => { setPeriod('1d'); setShowCustomPicker(false); }}
                  >
                    Today
                  </button>
                  <button
                    className={`btn btn-sm ${period === '7d' && !showCustomPicker ? 'btn-primary' : 'btn-outline-secondary'}`}
                    onClick={() => { setPeriod('7d'); setShowCustomPicker(false); }}
                  >
                    7 Days
                  </button>
                  <button
                    className={`btn btn-sm ${period === '30d' && !showCustomPicker ? 'btn-primary' : 'btn-outline-secondary'}`}
                    onClick={() => { setPeriod('30d'); setShowCustomPicker(false); }}
                  >
                    30 Days
                  </button>
                  <button
                    className={`btn btn-sm ${period === '90d' && !showCustomPicker ? 'btn-primary' : 'btn-outline-secondary'}`}
                    onClick={() => { setPeriod('90d'); setShowCustomPicker(false); }}
                  >
                    90 Days
                  </button>
                  <button
                    className={`btn btn-sm ${showCustomPicker ? 'btn-primary' : 'btn-outline-secondary'}`}
                    onClick={() => setShowCustomPicker(v => !v)}
                  >
                    <i className="bi bi-calendar-range me-1"></i>Custom
                  </button>
                </div>
                {/* Custom date picker inline */}
                {showCustomPicker && (
                  <DateRangePicker
                    startDate={customStart}
                    endDate={customEnd}
                    onChange={(s, e) => { setCustomStart(s); setCustomEnd(e); }}
                    onApply={() => {
                      if (customStart && customEnd) {
                        fetchEnhancedStats(
                          customStart.toISOString().split('T')[0],
                          customEnd.toISOString().split('T')[0]
                        );
                      }
                    }}
                    onClear={() => { setCustomStart(null); setCustomEnd(null); setShowCustomPicker(false); fetchEnhancedStats(); }}
                    maxDate={new Date()}
                    showActions
                  />
                )}
                {/* Granularity Toggle */}
                {!showCustomPicker && (
                  <div className="btn-group">
                    <button
                      className={`btn btn-sm ${granularity === 'daily' ? 'btn-primary' : 'btn-outline-secondary'}`}
                      onClick={() => setGranularity('daily')}
                    >
                      Daily
                    </button>
                    <button
                      className={`btn btn-sm ${granularity === 'monthly' ? 'btn-primary' : 'btn-outline-secondary'}`}
                      onClick={() => setGranularity('monthly')}
                    >
                      Monthly
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Live Visitors Hub */}
        {currentAsset && (
          <div className="row mb-4">
            <div className="col-12 col-lg-6">
              <LiveVisitorsHub assetId={currentAsset.id} assetName={currentAsset.name} />
            </div>
            <div className="col-12 col-lg-6">
              {/* Quick Stats Summary */}
              <div className="card border-0 shadow-sm h-100">
                <div className="card-header bg-white border-0 py-3">
                  <h5 className="mb-0">
                    <i className="bi bi-speedometer2 me-2"></i>
                    Quick Stats ({period})
                  </h5>
                </div>
                <div className="card-body">
                  <div className="row g-3">
                    <div className="col-6">
                      <div className="p-3 bg-primary bg-opacity-10 rounded">
                        <div className="d-flex align-items-center mb-2">
                          <i className="bi bi-people text-primary fs-4 me-2"></i>
                          <span className="text-muted small">Total Visitors</span>
                        </div>
                        <h3 className="mb-0 text-primary">{formatNumber(stats?.visitors || 0)}</h3>
                      </div>
                    </div>
                    <div className="col-6">
                      <div className="p-3 bg-success bg-opacity-10 rounded">
                        <div className="d-flex align-items-center mb-2">
                          <i className="bi bi-eye text-success fs-4 me-2"></i>
                          <span className="text-muted small">Page Views</span>
                        </div>
                        <h3 className="mb-0 text-success">{formatNumber(stats?.pageViews || 0)}</h3>
                      </div>
                    </div>
                    <div className="col-6">
                      <div className="p-3 bg-warning bg-opacity-10 rounded">
                        <div className="d-flex align-items-center mb-2">
                          <i className="bi bi-arrow-return-left text-warning fs-4 me-2"></i>
                          <span className="text-muted small">Bounce Rate</span>
                        </div>
                        <h3 className="mb-0 text-warning">{stats?.bounceRate?.toFixed(1) || 0}%</h3>
                      </div>
                    </div>
                    <div className="col-6">
                      <div className="p-3 bg-info bg-opacity-10 rounded">
                        <div className="d-flex align-items-center mb-2">
                          <i className="bi bi-clock text-info fs-4 me-2"></i>
                          <span className="text-muted small">Avg. Duration</span>
                        </div>
                        <h3 className="mb-0 text-info">{formatDuration(stats?.avgSessionDuration || 0)}</h3>
                      </div>
                    </div>
                  </div>
                  {/* Growth Score Progress */}
                  <div className="mt-4">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <span className="text-muted small">Growth Score</span>
                      <span className={`badge bg-${getGrowthScoreColor(stats?.growthScore || 0)}`}>
                        {stats?.growthScore || 0}/100
                      </span>
                    </div>
                    <div className="progress" style={{ height: '10px' }}>
                      <div
                        className={`progress-bar bg-${getGrowthScoreColor(stats?.growthScore || 0)}`}
                        role="progressbar"
                        style={{ width: `${stats?.growthScore || 0}%` }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Active Campaigns Widget */}
        {currentAsset && (
          <div className="row mb-4">
            <div className="col-12">
              <div className="card border-0 shadow-sm">
                <div className="card-header bg-white d-flex justify-content-between align-items-center">
                  <h6 className="mb-0">
                    <i className="bi bi-megaphone-fill me-2 text-primary"></i>Active Campaigns
                  </h6>
                  <Link to="/analytics/campaigns" className="btn btn-sm btn-outline-primary">
                    <i className="bi bi-grid me-1"></i>View All
                  </Link>
                </div>
                <div className="card-body p-0">
                  {activeCampaigns.length === 0 ? (
                    <div className="text-center py-4 text-muted">
                      <i className="bi bi-megaphone d-block mb-2"></i>
                      <small>No active campaigns. <Link to="/analytics/campaigns">Create one</Link></small>
                    </div>
                  ) : (
                    <div className="table-responsive">
                      <table className="table table-hover mb-0">
                        <tbody>
                          {activeCampaigns.map(campaign => (
                            <tr key={campaign.id}>
                              <td>
                                <span className="fw-medium">{campaign.name}</span>
                                <small className="text-muted font-monospace ms-2">?cmp={campaign.slug}</small>
                              </td>
                              <td className="text-end">
                                {campaign.visitors_count != null && (
                                  <span className="text-muted small">
                                    <i className="bi bi-people me-1"></i>{campaign.visitors_count} visitors
                                  </span>
                                )}
                              </td>
                              <td className="text-end">
                                <Link to={`/analytics/campaigns/${campaign.id}`} className="btn btn-sm btn-outline-secondary">
                                  View
                                </Link>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Stats Cards Row 1 */}
        <div className="row g-3 mb-4">
          <div className="col-6 col-md-4 col-xl-2">
            <div className="card border-0 shadow-sm h-100">
              <div className="card-body">
                <div className="d-flex align-items-center">
                  <div className="p-2 bg-primary bg-opacity-10 text-primary rounded me-2">
                    <i className="bi bi-people fs-5"></i>
                  </div>
                  <div>
                    <p className="text-muted small mb-0">Visitors</p>
                    <h4 className="h5 mb-0">{formatNumber(stats?.visitors || 0)}</h4>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="col-6 col-md-4 col-xl-2">
            <div className="card border-0 shadow-sm h-100">
              <div className="card-body">
                <div className="d-flex align-items-center">
                  <div className="p-2 bg-success bg-opacity-10 text-success rounded me-2">
                    <i className="bi bi-eye fs-5"></i>
                  </div>
                  <div>
                    <p className="text-muted small mb-0">Page Views</p>
                    <h4 className="h5 mb-0">{formatNumber(stats?.pageViews || 0)}</h4>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="col-6 col-md-4 col-xl-2">
            <div className="card border-0 shadow-sm h-100">
              <div className="card-body">
                <div className="d-flex align-items-center">
                  <div className="p-2 bg-warning bg-opacity-10 text-warning rounded me-2">
                    <i className="bi bi-lightning fs-5"></i>
                  </div>
                  <div>
                    <p className="text-muted small mb-0">Events</p>
                    <h4 className="h5 mb-0">{formatNumber(stats?.events || 0)}</h4>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="col-6 col-md-4 col-xl-2">
            <div className="card border-0 shadow-sm h-100">
              <div className="card-body">
                <div className="d-flex align-items-center">
                  <div className="p-2 bg-info bg-opacity-10 text-info rounded me-2">
                    <i className="bi bi-arrow-return-left fs-5"></i>
                  </div>
                  <div>
                    <p className="text-muted small mb-0">Bounce Rate</p>
                    <h4 className="h5 mb-0">{stats?.bounceRate?.toFixed(1) || 0}%</h4>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="col-6 col-md-4 col-xl-2">
            <div className="card border-0 shadow-sm h-100">
              <div className="card-body">
                <div className="d-flex align-items-center">
                  <div className="p-2 bg-secondary bg-opacity-10 text-secondary rounded me-2">
                    <i className="bi bi-clock fs-5"></i>
                  </div>
                  <div>
                    <p className="text-muted small mb-0">Avg. Duration</p>
                    <h4 className="h6 mb-0">{formatDuration(stats?.avgSessionDuration || 0)}</h4>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="col-6 col-md-4 col-xl-2">
            <div className="card border-0 shadow-sm h-100">
              <div className="card-body">
                <div className="d-flex align-items-center">
                  <div className={`p-2 bg-${getGrowthScoreColor(stats?.growthScore || 0)} bg-opacity-10 text-${getGrowthScoreColor(stats?.growthScore || 0)} rounded me-2`}>
                    <i className="bi bi-graph-up-arrow fs-5"></i>
                  </div>
                  <div>
                    <p className="text-muted small mb-0">Growth Score</p>
                    <h4 className="h5 mb-0">{stats?.growthScore || 0}/100</h4>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Conversion Summary Table */}
        <div className="row mb-4">
          <div className="col-12">
            <div className="card border-0 shadow-sm">
              <div className="card-header bg-white d-flex justify-content-between align-items-center py-3">
                <h5 className="mb-0">
                  <i className="bi bi-trophy-fill text-warning me-2"></i>
                  Conversion Summary
                  {conversionStats?.has_goals && conversionStats.goal_event_names.length > 0 && (
                    <small className="text-muted fw-normal ms-2 fs-6">Goal: {conversionStats.goal_event_names.join(', ')}</small>
                  )}
                </h5>
                <Link to="/events" className="btn btn-sm btn-outline-secondary">
                  <i className="bi bi-pencil-square me-1"></i>Manage Events
                </Link>
              </div>
              <div className="card-body p-0">
                <div className="table-responsive">
                  <table className="table mb-0">
                    <thead className="table-light">
                      <tr>
                        <th>Metric</th>
                        <th className="text-end">Count</th>
                        <th className="text-end">Rate</th>
                        <th>Notes</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td><i className="bi bi-eye me-2 text-primary"></i>Page Views</td>
                        <td className="text-end fw-semibold">{stats ? new Intl.NumberFormat().format(stats.pageViews) : '—'}</td>
                        <td className="text-end text-muted">—</td>
                        <td><small className="text-muted">Total pages loaded in period</small></td>
                      </tr>
                      <tr>
                        <td><i className="bi bi-people me-2 text-success"></i>Unique Visitors</td>
                        <td className="text-end fw-semibold">{stats ? new Intl.NumberFormat().format(stats.visitors) : '—'}</td>
                        <td className="text-end text-muted">100%</td>
                        <td><small className="text-muted">Distinct visitors in period</small></td>
                      </tr>
                      <tr>
                        <td><i className="bi bi-trophy-fill me-2 text-warning"></i>Converted</td>
                        <td className="text-end fw-semibold">
                          {conversionStats?.has_goals
                            ? new Intl.NumberFormat().format(conversionStats.converted_visitors)
                            : <span className="text-muted fst-italic">—</span>}
                        </td>
                        <td className="text-end">
                          {conversionStats?.has_goals ? (
                            <span className={`fw-semibold ${conversionStats.conversion_rate > 0 ? 'text-success' : 'text-muted'}`}>
                              {conversionStats.conversion_rate}%
                            </span>
                          ) : (
                            <span className="text-muted">—</span>
                          )}
                        </td>
                        <td>
                          <small>
                            {conversionStats?.has_goals
                              ? <span className="text-muted">Visitors who completed a goal event</span>
                              : <span className="text-warning">
                                  <i className="bi bi-exclamation-triangle me-1"></i>
                                  <Link to="/events" className="text-warning">Register a goal event</Link> to track conversion rate
                                </span>}
                          </small>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Charts Row - Main Charts */}
        <div className="row g-4 mb-4">
          {/* Page Views Trend Chart */}
          <div className="col-12 col-lg-8">
            <div className="card border-0 shadow-sm h-100">
              <div className="card-header bg-white border-0 py-3">
                <h5 className="mb-0">
                  <i className="bi bi-graph-up me-2"></i>
                  Page Views & Visitors Trend
                </h5>
              </div>
              <div className="card-body">
                {stats?.pageViewsTrend && stats.pageViewsTrend.length > 0 ? (
                  <div style={{ height: '320px' }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={stats.pageViewsTrend} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                        <defs>
                          <linearGradient id="colorViews" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3}/>
                            <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                          </linearGradient>
                          <linearGradient id="colorVisitors" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#10b981" stopOpacity={0.3}/>
                            <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis
                          dataKey="date"
                          tickFormatter={(value) => formatDate(value)}
                          tick={{ fill: '#6b7280', fontSize: 11 }}
                        />
                        <YAxis tick={{ fill: '#6b7280', fontSize: 11 }} />
                        <Tooltip
                          labelFormatter={(label) => formatDate(label)}
                          contentStyle={{ borderRadius: '8px', border: '1px solid #e5e7eb' }}
                        />
                        <Legend />
                        <Area
                          type="monotone"
                          dataKey="views"
                          name="Page Views"
                          stroke="#3b82f6"
                          fillOpacity={1}
                          fill="url(#colorViews)"
                        />
                        <Area
                          type="monotone"
                          dataKey="uniqueVisitors"
                          name="Unique Visitors"
                          stroke="#10b981"
                          fillOpacity={1}
                          fill="url(#colorVisitors)"
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <div className="text-center text-muted py-5">
                    <i className="bi bi-graph-up fs-1 d-block mb-3"></i>
                    <p>No trend data available for this period</p>
                    <small>Data will appear once visitors start using your website</small>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Country Distribution - List Left, Pie Right */}
          <div className="col-12 col-lg-4">
            <div className="card border-0 shadow-sm h-100">
              <div className="card-header bg-white border-0 py-3">
                <h5 className="mb-0">
                  <i className="bi bi-globe me-2"></i>
                  Top Countries
                </h5>
              </div>
              <div className="card-body">
                {stats?.countryStats && stats.countryStats.length > 0 ? (
                  <div className="d-flex" style={{ height: '280px' }}>
                    {/* Country List - Left Side */}
                    <div className="flex-grow-1 pe-2 overflow-auto" style={{ maxHeight: '280px' }}>
                      {stats.countryStats.slice(0, 10).map((country, index) => (
                        <div key={index} className="d-flex justify-content-between align-items-center py-2 border-bottom">
                          <div className="d-flex align-items-center">
                            <span
                              className="d-inline-block rounded-circle me-2 flex-shrink-0"
                              style={{ width: '10px', height: '10px', backgroundColor: CHART_COLORS[index % CHART_COLORS.length] }}
                            ></span>
                            <span className="small text-truncate" style={{ maxWidth: '120px' }}>
                              {country.country || 'Unknown'}
                            </span>
                          </div>
                          <div className="text-end flex-shrink-0">
                            <span className="badge bg-light text-dark">{country.percentage}%</span>
                            <span className="text-muted small ms-1">({formatCompact(country.count)})</span>
                          </div>
                        </div>
                      ))}
                    </div>
                    {/* Pie Chart - Right Side */}
                    <div style={{ width: '120px', flexShrink: 0 }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={stats.countryStats.slice(0, 8)}
                            dataKey="count"
                            nameKey="country"
                            cx="50%"
                            cy="50%"
                            innerRadius={30}
                            outerRadius={55}
                          >
                            {stats.countryStats.slice(0, 8).map((entry, index) => (
                              <Cell key={index} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                            ))}
                          </Pie>
                          <Tooltip formatter={(value: any) => formatNumber(value || 0)} />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                ) : (
                  <div className="text-center text-muted py-5">
                    <i className="bi bi-geo-alt fs-1 d-block mb-3"></i>
                    <p>No country data available</p>
                    <small>Geographic data will appear with visitor tracking</small>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Devices & Traffic Sources Row */}
        <div className="row g-4 mb-4">
          {/* Devices Chart */}
          <div className="col-12 col-md-6 col-lg-4">
            <div className="card border-0 shadow-sm h-100">
              <div className="card-header bg-white border-0 py-3">
                <h5 className="mb-0">
                  <i className="bi bi-laptop me-2"></i>
                  Devices
                </h5>
              </div>
              <div className="card-body">
                {stats?.deviceStats && stats.deviceStats.length > 0 ? (
                  <>
                    <div style={{ height: '200px' }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={stats.deviceStats}
                            dataKey="count"
                            nameKey="device"
                            cx="50%"
                            cy="50%"
                            innerRadius={40}
                            outerRadius={70}
                          >
                            {stats.deviceStats.map((entry, index) => (
                              <Cell key={index} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                            ))}
                          </Pie>
                          <Tooltip formatter={(value: any) => formatNumber(value || 0)} />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                    <div className="mt-3">
                      {stats.deviceStats.slice(0, 4).map((device, index) => (
                        <div key={index} className="d-flex justify-content-between align-items-center mb-2">
                          <div className="d-flex align-items-center">
                            <span
                              className="d-inline-block rounded-circle me-2"
                              style={{ width: '10px', height: '10px', backgroundColor: CHART_COLORS[index % CHART_COLORS.length] }}
                            ></span>
                            <span className="small">
                              <i className={`bi ${device.device === 'Mobile' ? 'bi-phone' : device.device === 'Tablet' ? 'bi-tablet' : 'bi-laptop'} me-1`}></i>
                              {device.device}
                            </span>
                          </div>
                          <span className="badge bg-light text-dark">{device.percentage}%</span>
                        </div>
                      ))}
                    </div>
                  </>
                ) : (
                  <div className="text-center text-muted py-5">
                    <i className="bi bi-laptop fs-1 d-block mb-3"></i>
                    <p>No device data available</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Traffic Sources Chart */}
          <div className="col-12 col-md-6 col-lg-4">
            <div className="card border-0 shadow-sm h-100">
              <div className="card-header bg-white border-0 py-3">
                <h5 className="mb-0">
                  <i className="bi bi-signpost-2 me-2"></i>
                  Traffic Sources
                </h5>
              </div>
              <div className="card-body">
                {stats?.trafficSources && stats.trafficSources.length > 0 ? (
                  <>
                    <div style={{ height: '200px' }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart
                          data={stats.trafficSources.slice(0, 6)}
                          layout="vertical"
                          margin={{ top: 5, right: 20, left: 60, bottom: 5 }}
                        >
                          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" horizontal={false} />
                          <XAxis type="number" tick={{ fill: '#6b7280', fontSize: 11 }} />
                          <YAxis
                            type="category"
                            dataKey="source"
                            tick={{ fill: '#6b7280', fontSize: 11 }}
                            width={55}
                          />
                          <Tooltip
                            formatter={(value: any) => formatNumber(value || 0)}
                            contentStyle={{ borderRadius: '8px', border: '1px solid #e5e7eb' }}
                          />
                          <Bar dataKey="count" fill="#7C3AED" radius={[0, 4, 4, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                    <div className="mt-2">
                      <small className="text-muted">
                        <i className="bi bi-info-circle me-1"></i>
                        Traffic from Google, Instagram, Facebook, and more
                      </small>
                    </div>
                  </>
                ) : (
                  <div className="text-center text-muted py-5">
                    <i className="bi bi-signpost fs-1 d-block mb-3"></i>
                    <p>No traffic source data available</p>
                    <small>Data will appear with visitor tracking</small>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Unique Visitors Breakdown */}
          <div className="col-12 col-md-6 col-lg-4">
            <div className="card border-0 shadow-sm h-100">
              <div className="card-header bg-white border-0 py-3">
                <h5 className="mb-0">
                  <i className="bi bi-person-check me-2"></i>
                  Visitor Types
                </h5>
              </div>
              <div className="card-body">
                {stats?.uniqueVisitorDetails && stats.uniqueVisitorDetails.total > 0 ? (
                  <div className="row align-items-center">
                    <div className="col-6">
                      <div style={{ height: '200px' }}>
                        <ResponsiveContainer width="100%" height="100%">
                          <PieChart>
                            <Pie
                              data={uniqueVisitorsData}
                              dataKey="value"
                              nameKey="name"
                              cx="50%"
                              cy="50%"
                              innerRadius={50}
                              outerRadius={80}
                            >
                              {uniqueVisitorsData.map((entry, index) => (
                                <Cell key={index} fill={entry.color} />
                              ))}
                            </Pie>
                            <Tooltip formatter={(value: any) => formatNumber(value || 0)} />
                          </PieChart>
                        </ResponsiveContainer>
                      </div>
                    </div>
                    <div className="col-6">
                      <div className="mb-3">
                        <div className="d-flex align-items-center mb-2">
                          <span className="badge me-2" style={{ backgroundColor: '#10B981', width: '12px', height: '12px', padding: 0 }}></span>
                          <span className="text-muted">New Visitors</span>
                        </div>
                        <h4 className="mb-0">{formatNumber(stats.uniqueVisitorDetails.new)}</h4>
                        <small className="text-muted">
                          {stats.uniqueVisitorDetails.total > 0
                            ? Math.round((stats.uniqueVisitorDetails.new / stats.uniqueVisitorDetails.total) * 100)
                            : 0}% of total
                        </small>
                      </div>
                      <div>
                        <div className="d-flex align-items-center mb-2">
                          <span className="badge me-2" style={{ backgroundColor: '#3B82F6', width: '12px', height: '12px', padding: 0 }}></span>
                          <span className="text-muted">Returning Visitors</span>
                        </div>
                        <h4 className="mb-0">{formatNumber(stats.uniqueVisitorDetails.returning)}</h4>
                        <small className="text-muted">
                          {stats.uniqueVisitorDetails.total > 0
                            ? Math.round((stats.uniqueVisitorDetails.returning / stats.uniqueVisitorDetails.total) * 100)
                            : 0}% of total
                        </small>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-center text-muted py-5">
                    <i className="bi bi-people fs-1 d-block mb-3"></i>
                    <p>No visitor data available</p>
                  </div>
                )}
              </div>
            </div>
          </div>

        </div>

        {/* Year-over-Year Comparison Row */}
        <div className="row g-4 mb-4">
          <div className="col-12">
            <div className="card border-0 shadow-sm">
              <div className="card-header bg-white border-0 py-3">
                <h5 className="mb-0">
                  <i className="bi bi-calendar-range me-2"></i>
                  Year-over-Year Comparison
                </h5>
              </div>
              <div className="card-body">
                {stats?.yearOverYear ? (
                  <div className="row align-items-center">
                    <div className="col-12 col-lg-8">
                      <div style={{ height: '250px' }}>
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart data={yoyData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                            <XAxis dataKey="metric" tick={{ fill: '#6b7280', fontSize: 12 }} />
                            <YAxis tick={{ fill: '#6b7280', fontSize: 11 }} />
                            <Tooltip
                              formatter={(value: any) => formatNumber(value || 0)}
                              contentStyle={{ borderRadius: '8px', border: '1px solid #e5e7eb' }}
                            />
                            <Legend />
                            <Bar dataKey="Current Period" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                            <Bar dataKey="Last Year" fill="#94a3b8" radius={[4, 4, 0, 0]} />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    </div>
                    <div className="col-12 col-lg-4">
                      <div className="row g-3">
                        <div className="col-6">
                          <div className="card border-0 bg-success bg-opacity-10">
                            <div className="card-body text-center py-3">
                              <small className="text-muted d-block mb-1">Visitor Change</small>
                              <span className={`h4 mb-0 ${stats.yearOverYear.visitorChange >= 0 ? 'text-success' : 'text-danger'}`}>
                                <i className={getChangeIcon(stats.yearOverYear.visitorChange) + ' me-1'}></i>
                                {stats.yearOverYear.visitorChange >= 0 ? '+' : ''}{stats.yearOverYear.visitorChange}%
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="col-6">
                          <div className="card border-0 bg-primary bg-opacity-10">
                            <div className="card-body text-center py-3">
                              <small className="text-muted d-block mb-1">Page View Change</small>
                              <span className={`h4 mb-0 ${stats.yearOverYear.pageViewChange >= 0 ? 'text-success' : 'text-danger'}`}>
                                <i className={getChangeIcon(stats.yearOverYear.pageViewChange) + ' me-1'}></i>
                                {stats.yearOverYear.pageViewChange >= 0 ? '+' : ''}{stats.yearOverYear.pageViewChange}%
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="col-12">
                          <div className="card border-0 bg-light">
                            <div className="card-body py-3">
                              <div className="d-flex justify-content-between mb-2">
                                <span className="text-muted small">Current Visitors</span>
                                <span className="fw-semibold">{formatNumber(stats.yearOverYear.currentVisitors)}</span>
                              </div>
                              <div className="d-flex justify-content-between mb-2">
                                <span className="text-muted small">Last Year Visitors</span>
                                <span className="fw-semibold">{formatNumber(stats.yearOverYear.lastYearVisitors)}</span>
                              </div>
                              <hr className="my-2" />
                              <div className="d-flex justify-content-between mb-2">
                                <span className="text-muted small">Current Page Views</span>
                                <span className="fw-semibold">{formatNumber(stats.yearOverYear.currentPageViews)}</span>
                              </div>
                              <div className="d-flex justify-content-between">
                                <span className="text-muted small">Last Year Page Views</span>
                                <span className="fw-semibold">{formatNumber(stats.yearOverYear.lastYearPageViews)}</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-center text-muted py-5">
                    <i className="bi bi-calendar-x fs-1 d-block mb-3"></i>
                    <p>Year-over-year data not available</p>
                    <small>Data will appear after one year of tracking</small>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Top 5 Pages */}
        <div className="row g-4">
          <div className="col-12">
            <div className="card border-0 shadow-sm">
              <div className="card-header bg-white border-0 py-3">
                <h5 className="mb-0">
                  <i className="bi bi-file-earmark-text me-2"></i>
                  Top 5 Most Visited Pages
                </h5>
              </div>
              <div className="card-body p-0">
                {stats?.topPages && stats.topPages.length > 0 ? (
                  <div className="table-responsive">
                    <table className="table table-hover align-middle mb-0">
                      <thead className="table-light">
                        <tr>
                          <th className="border-0 ps-4" style={{ width: '5%' }}>#</th>
                          <th className="border-0" style={{ width: '50%' }}>Page</th>
                          <th className="border-0" style={{ width: '25%' }}>Views Distribution</th>
                          <th className="border-0 text-end" style={{ width: '10%' }}>Views</th>
                          <th className="border-0 text-end pe-4" style={{ width: '10%' }}>Share</th>
                        </tr>
                      </thead>
                      <tbody>
                        {stats.topPages.map((page, index) => (
                          <tr key={index}>
                            <td className="ps-4">
                              <span className={`badge bg-${index === 0 ? 'primary' : index === 1 ? 'secondary' : 'light text-dark'}`}>
                                {index + 1}
                              </span>
                            </td>
                            <td>
                              <div className="text-truncate fw-medium" style={{ maxWidth: '400px' }}>
                                {page.title}
                              </div>
                              <small className="text-muted text-truncate d-block" style={{ maxWidth: '400px' }}>
                                {page.url}
                              </small>
                            </td>
                            <td>
                              <div className="progress" style={{ height: '8px' }}>
                                <div
                                  className="progress-bar"
                                  style={{
                                    width: `${page.percentage}%`,
                                    backgroundColor: CHART_COLORS[index % CHART_COLORS.length]
                                  }}
                                />
                              </div>
                            </td>
                            <td className="text-end">
                              <span className="fw-semibold">{formatNumber(page.views)}</span>
                            </td>
                            <td className="text-end pe-4">
                              <span className="badge bg-primary bg-opacity-10 text-primary">
                                {page.percentage}%
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-center text-muted py-5">
                    <i className="bi bi-file-earmark-text fs-1 d-block mb-3"></i>
                    <p className="mb-0">No page view data available</p>
                    <small>Data will appear once visitors start browsing your website</small>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default SummaryPage;
